<template>
  <PageContainer>
    <IntegrationPageHeader
      :tile="definition.tile"
      :name="definition.name"
      description="Generate a fresh door code for every self-guided showing"
      :connected="isConnected"
    >
      <template #actions v-if="isConnected && !investorStore.isAdmin">
        <Text variant="body-small" color="secondary">Only an admin can sync or disconnect CodeBox.</Text>
      </template>
      <template #actions v-else-if="isConnected">
        <Button variant="ghost" size="medium" :disabled="sync.isLoading.value" @click="sync.mutate()">
          <View gap="xs" align="center">
            <Spinner v-if="sync.isLoading.value" size="small" />
            <Icon v-else name="refresh" />
            <span>Sync lockboxes</span>
          </View>
        </Button>
        <Button variant="secondary" appearance="danger" size="medium" @click="disconnectModalRef?.open()">
          Disconnect
        </Button>
      </template>
    </IntegrationPageHeader>

    <CodeboxConnectCard v-if="!isConnected && !status.isPending.value && investorStore.isAdmin" />

    <Card v-else-if="!isConnected && !status.isPending.value" size="large">
      <View direction="column" gap="sm">
        <Text variant="h3">CodeBox isn't connected</Text>
        <Text variant="body-medium" color="secondary">
          The CodeBox account belongs to the whole company, so an admin connects it. Ask one of your admins.
        </Text>
      </View>
    </Card>

    <View v-else-if="status.isPending.value" justify="center" padding="xl">
      <LoadingIndicator />
    </View>

    <IntegrationPageLayout v-else>
      <Card size="large">
        <View direction="column" gap="md">
          <View justify="space-between" align="center" gap="sm">
            <View gap="sm" align="center">
              <Text variant="h3">Lockboxes</Text>
              <Text variant="h3" color="tertiary">{{ lockboxes.length }}</Text>
            </View>
            <Text variant="body-medium" color="tertiary">{{ assignedSummary }}</Text>
          </View>

          <Hint v-if="!lockboxes.length">No lockboxes yet. Add them in the CodeBox portal, then {{ syncHint }}.</Hint>

          <View v-else direction="column" gap="xs" data-testid="codebox-lockbox-list">
            <View
              v-for="lockbox in lockboxes"
              :key="lockbox.id"
              gap="md"
              align="center"
              class="lockbox-row"
              :data-testid="`codebox-row-${lockbox.serialNumber}`"
            >
              <RouterLink
                v-if="coverImageFor(lockbox)"
                :to="{
                  name: AppRouteNames.INVESTOR_PROPERTY_DASHBOARD,
                  params: { propertyId: lockbox.property!.id },
                }"
                class="lockbox-photo-link"
                :title="lockbox.property?.displayName"
              >
                <img :src="coverImageFor(lockbox)" :alt="lockbox.property?.displayName" class="lockbox-photo" />
              </RouterLink>
              <IconTile v-else icon="key" size="small" colored-icon :color="lockbox.property ? 'green' : 'neutral'" />

              <View direction="column" gap="xs" class="lockbox-detail">
                <Text variant="label-medium">{{ lockbox.serialNumber }}</Text>
                <Text variant="body-medium" color="tertiary">
                  {{ lockbox.property?.displayName ?? 'Not on a property' }}
                </Text>
              </View>

              <View gap="sm" align="center" class="lockbox-actions">
                <Select
                  v-if="!lockbox.property"
                  :model-value="null"
                  @update:model-value="(propertyId) => onAssign(lockbox, propertyId as string)"
                  :data-testid="`codebox-assign-${lockbox.serialNumber}`"
                >
                  <option :value="null" disabled>Assign to…</option>
                  <option v-for="property in assignableProperties" :key="property.id" :value="property.id">
                    {{ property.displayName }}
                  </option>
                </Select>

                <Button
                  v-else
                  variant="ghost"
                  size="small"
                  :disabled="busyCodeboxId === lockbox.id"
                  @click="onUnassign(lockbox)"
                  :data-testid="`codebox-unassign-${lockbox.serialNumber}`"
                >
                  <View gap="xs" align="center">
                    <Spinner v-if="busyCodeboxId === lockbox.id" size="small" />
                    <span>Remove</span>
                  </View>
                </Button>

                <Button variant="ghost" size="small" @click="openHistory(lockbox)">Log</Button>
              </View>
            </View>
          </View>

          <Text v-if="lockboxes.length" variant="body-small" color="tertiary">
            Missing a lockbox? Add it in the CodeBox portal, then {{ syncHint }}.
          </Text>
        </View>
      </Card>

      <Card v-if="lockboxes.length" size="large">
        <View direction="column" gap="sm">
          <Text variant="h3">Recent activity</Text>

          <Text v-if="!recentActivity.length" variant="body-medium" color="tertiary">
            Nothing yet. Activity shows up here once a renter gets a door code.
          </Text>

          <View v-else direction="column" data-testid="codebox-activity">
            <View v-for="(entry, index) in recentActivity" :key="index" gap="sm" align="center" class="activity-row">
              <Icon name="clock-rewind" class="activity-icon" />
              <Text variant="body-medium" class="activity-text">
                Door code minted for a showing at
                <span class="activity-property">{{ entry.propertyName ?? `lockbox ${entry.serialNumber}` }}</span>
              </Text>
              <Text variant="body-small" color="tertiary" class="activity-date">
                {{ formatActivityDate(entry.date) }}
              </Text>
            </View>
          </View>
        </View>
      </Card>

      <template #rail>
        <IntegrationDetailsCard :details="details">
          <Divider />

          <!-- Link's own :href overrides a plain href attribute, so the URL must go through `to`. -->
          <Link to="https://codeboxinc.com" target="_blank" rel="noopener">
            <View gap="xs" align="center">
              <Icon name="link-01" />
              <span>Open CodeBox portal</span>
            </View>
          </Link>
        </IntegrationDetailsCard>

        <Card size="large">
          <View direction="column" gap="sm">
            <Text variant="h5">How it works</Text>
            <Text variant="body-medium" color="secondary">
              Your portal credentials are stored encrypted and only used to mint codes for your showings. Each property
              holds one lockbox; the property's access method follows the lockbox automatically.
            </Text>
          </View>
        </Card>
      </template>
    </IntegrationPageLayout>

    <CodeboxDisconnectModal ref="disconnectModalRef" @disconnected="goBack" />
    <CodeboxHistoryModal
      ref="historyModalRef"
      :codebox-id="historyCodebox?.id ?? null"
      :serial-number="historyCodebox?.serialNumber ?? null"
    />
  </PageContainer>
</template>

<script setup lang="ts">
import { format, isValid, parseISO } from 'date-fns';
import { computed, nextTick, ref } from 'vue';
import { RouterLink, useRouter } from 'vue-router';

import {
  Button,
  Card,
  Divider,
  Hint,
  Icon,
  IconTile,
  Link,
  LoadingIndicator,
  PageContainer,
  Select,
  Spinner,
  Text,
  useAlertsStore,
  View,
} from '@tallo/design-system';
import { useInvestorStore } from '@tallo/investor/investor';
import { AppRouteNames } from '@tallo/investor/navigation';
import {
  CodeboxDto,
  propertyService,
  useAssignCodebox,
  useCodeboxActivityQuery,
  useCodeboxIntegrationQuery,
  useCodeboxesQuery,
  usePortfolioQuery,
  useSyncCodeboxes,
  useUnassignCodebox,
} from '@tallo/investor/property';
import { SelfGuidedAccessMethod } from '@tallo/property';

import CodeboxConnectCard from './codebox-connect-card.vue';
import CodeboxDisconnectModal from './codebox/codebox-disconnect-modal.vue';
import CodeboxHistoryModal from './codebox/codebox-history-modal.vue';
import IntegrationDetailsCard from './integration-details-card.vue';
import IntegrationPageHeader from './integration-page-header.vue';
import IntegrationPageLayout from './integration-page-layout.vue';
import { integrations } from './integrations.config';

const router = useRouter();

// One registry entry drives both the marketplace card and this page, so the branding cannot drift.
const definition = integrations.find((integration) => integration.id === 'codebox')!;

const investorStore = useInvestorStore();
// Syncing is an admin's button, so a member is pointed at who can press it
const syncHint = computed(() => (investorStore.isAdmin ? 'hit Sync lockboxes' : 'ask an admin to sync lockboxes'));
const status = useCodeboxIntegrationQuery();
const isConnected = computed(() => Boolean(status.data.value?.connected));

const codeboxes = useCodeboxesQuery(() => isConnected.value);
const lockboxes = computed(() => [...(codeboxes.data.value ?? [])].sort(bySerialNumber));

const portfolio = usePortfolioQuery();

const sync = useSyncCodeboxes();
const assign = useAssignCodebox();
const unassign = useUnassignCodebox();

const busyCodeboxId = ref<string | null>(null);
const disconnectModalRef = ref<InstanceType<typeof CodeboxDisconnectModal> | null>(null);
const historyModalRef = ref<InstanceType<typeof CodeboxHistoryModal> | null>(null);
const historyCodebox = ref<CodeboxDto | null>(null);

const assignedCount = computed(() => lockboxes.value.filter((lockbox) => lockbox.property).length);
const assignedSummary = computed(() => `${assignedCount.value} of ${lockboxes.value.length} on a property`);

const propertyIdsWithLockbox = computed(
  () => new Set(lockboxes.value.map((lockbox) => lockbox.property?.id).filter(Boolean)),
);

const assignableProperties = computed(() =>
  portfolio.properties.value.filter((property) => !propertyIdsWithLockbox.value.has(property.id)),
);

const details = computed(() => [
  { label: 'Account', value: status.data.value?.username ?? '—' },
  { label: 'Category', value: 'Property access' },
  { label: 'Lockboxes', value: String(lockboxes.value.length) },
  { label: 'Connected', value: formatDate(status.data.value?.connectedAt) },
]);

const activity = useCodeboxActivityQuery(() => isConnected.value);
const recentActivity = computed(() => activity.data.value ?? []);

/** CodeBox sends US-style dates (02/02/2026), which parseISO cannot read. */
function formatActivityDate(value: string): string {
  const iso = parseISO(value);
  if (isValid(iso)) return format(iso, 'MMM d, yyyy');

  const loose = new Date(value);
  return isValid(loose) ? format(loose, 'MMM d, yyyy') : value;
}

/** The lockbox only carries the property's id and name, so the photo comes from the portfolio. */
function coverImageFor(lockbox: CodeboxDto): string | undefined {
  if (!lockbox.property) return undefined;

  return portfolio.properties.value.find((property) => property.id === lockbox.property!.id)?.coverImage;
}

function bySerialNumber(a: CodeboxDto, b: CodeboxDto): number {
  return a.serialNumber.localeCompare(b.serialNumber, undefined, { numeric: true });
}

function formatDate(value?: string | null): string {
  if (!value) return '—';

  const parsed = parseISO(value);
  return isValid(parsed) ? format(parsed, 'MMM d, yyyy') : value;
}

async function onAssign(lockbox: CodeboxDto, propertyId: string) {
  if (!propertyId) return;

  busyCodeboxId.value = lockbox.id;

  try {
    await assign.mutateAsync({ codeboxId: lockbox.id, propertyId });
    await propertyService.updateProperty(propertyId, { selfGuidedAccessMethod: SelfGuidedAccessMethod.CODEBOX });
    useAlertsStore().success('Lockbox assigned');
  } finally {
    busyCodeboxId.value = null;
  }
}

async function onUnassign(lockbox: CodeboxDto) {
  const propertyId = lockbox.property?.id;

  busyCodeboxId.value = lockbox.id;

  try {
    await unassign.mutateAsync({ codeboxId: lockbox.id });

    if (propertyId) {
      await propertyService.updateProperty(propertyId, { selfGuidedAccessMethod: null });
    }

    useAlertsStore().success('Lockbox removed from the property');
  } finally {
    busyCodeboxId.value = null;
  }
}

function openHistory(lockbox: CodeboxDto) {
  historyCodebox.value = lockbox;
  nextTick(() => historyModalRef.value?.open());
}

function goBack() {
  router.push({ name: AppRouteNames.INVESTOR_INTEGRATIONS });
}
</script>

<style scoped lang="scss">
@import 'styles/mixins/layout.mixins';

.lockbox-row {
  padding: var(--t-spacing-sm);
  border-radius: var(--t-border-radius-md);
  background-color: var(--t-color-neutral-50);
}

.lockbox-detail {
  flex: 1;
  min-width: 0;
}

// Assigning swaps the 3rem select for 2rem buttons; pinning the cell to the select's height keeps
// the row from jumping when the state flips.
.lockbox-actions {
  min-height: var(--t-spacing-xxl);
}

.lockbox-photo-link {
  display: block;
  flex: 0 0 auto;
  border-radius: var(--t-border-radius-sm);
}

// Matches IconTile's small size so rows stay aligned whether they show a photo or the key tile.
.lockbox-photo {
  width: 2.25rem;
  height: 2.25rem;
  flex: 0 0 auto;
  border-radius: var(--t-border-radius-sm);
  object-fit: cover;
  display: block;
}

.activity-row {
  padding: var(--t-spacing-sm) 0;

  & + & {
    border-top: 1px solid var(--t-color-neutral-100);
  }
}

.activity-icon {
  color: var(--t-color-neutral-500);
}

.activity-text {
  flex: 1;
  min-width: 0;
}

.activity-property {
  font-weight: 500;
}

@include screen-max-md {
  // Actions drop below the address rather than squeezing it into a three-line column.
  .lockbox-row {
    flex-wrap: wrap;
    row-gap: var(--t-spacing-xs);
  }

  .lockbox-actions {
    flex: 1 0 100%;
    justify-content: flex-end;
  }

  .activity-date {
    margin-inline-start: auto;
  }

  .activity-row {
    flex-wrap: wrap;
  }
}
</style>
